'use client'

import React from 'react'
import { Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import Wallet from '@/utils/wallet'
import { offersEnabled } from '@/utils/offers'
import useTwetchNotifications from '@/app/alkanes/lib/use-twetch-notifications'
import { SocialNotificationRow } from '@/app/alkanes/components/notifications-bell'
import useOfferNotifications from '@/components/offers/use-offer-notifications'
import {
	ManageOffersLink,
	OfferNotificationRow,
	type OfferNotificationAction
} from '@/components/offers/offer-notifications'
import ReviewOfferModal from '@/components/offers/review-offer-modal'
import CancelOfferModal from '@/components/offers/cancel-offer-modal'
import MakeOfferModal from '@/components/offers/make-offer-modal'
import type { Offer } from '@/utils/offers'

type Tab = 'all' | 'offers' | 'social'

/**
 * One bell for everything that happened to the wallet: offers waiting on its
 * items and bids it won, plus Social replies, likes and follows. All is the
 * default; the tabs narrow to one source. Each source keeps its own seen
 * marks, and both are marked seen when the panel closes.
 */
export default function NotificationsBell({ className }: { className?: string }) {
	const hydrated = useIsHydrated()
	const loggedIn = useLoggedIn()
	const address = hydrated && loggedIn ? Wallet.getTaprootAddress() : null
	const offersOn = hydrated && offersEnabled() && !!address
	const social = useTwetchNotifications()
	const offers = useOfferNotifications(offersOn ? address : null)
	const [open, setOpen] = React.useState(false)
	const [tab, setTab] = React.useState<Tab>('all')
	const [now, setNow] = React.useState(() => Date.now())
	// Dialogs live outside the popover: a click inside a portaled dialog counts
	// as "outside" to the popover, which would otherwise close and unmount them.
	const [pending, setPending] = React.useState<OfferNotificationAction | null>(null)
	// After an update places the new bid, the replaced one goes to refund.
	const [replacing, setReplacing] = React.useState<Offer | null>(null)
	const act = (action: OfferNotificationAction) => {
		setOpen(false)
		setPending(action)
	}

	const socialOn = social.userId !== null
	// The bell is part of the signed-in header from the first paint: it never
	// blinks out while the wallet hydrates or the sources load. It only
	// disappears when the wallet is signed out.
	if (!loggedIn) return null

	type Entry =
		| { source: 'social'; at: number; key: string; node: React.ReactNode }
		| { source: 'offers'; at: number; key: string; node: React.ReactNode }
	const done = () => {
		setOpen(false)
		social.markSeen()
		offers.markSeen()
	}
	const entries: Entry[] = [
		...(socialOn
			? social.items.map((item) => ({
					source: 'social' as const,
					at: item.createdAtMs,
					key: `social:${item.id}`,
					node: (
						<SocialNotificationRow
							item={item}
							actor={social.users[String(item.actorUserId)]}
							post={item.postId ? social.posts[String(item.postId)] : undefined}
							actionPost={item.actionPostId ? social.posts[String(item.actionPostId)] : undefined}
							unread={social.isUnread(item)}
							now={now}
							onDone={done}
						/>
					)
			  }))
			: []),
		...(offersOn
			? offers.items.map((item) => ({
					source: 'offers' as const,
					at: item.at,
					key: item.id,
					node: (
						<OfferNotificationRow item={item} unread={offers.isUnread(item)} onDone={done} onAction={act} />
					)
			  }))
			: [])
	]
		.filter((e) => tab === 'all' || e.source === tab)
		.sort((a, b) => b.at - a.at)
	const unread = (socialOn ? social.unread : 0) + (offersOn ? offers.unread : 0)
	const loading = (socialOn && social.loading) || (offersOn && offers.loading)
	const tabs: Array<{ id: Tab; label: string; count: number }> = [
		{ id: 'all', label: 'All', count: unread },
		...(offersOn ? [{ id: 'offers' as const, label: 'Offers', count: offers.unread }] : []),
		...(socialOn ? [{ id: 'social' as const, label: 'Social', count: social.unread }] : [])
	]
	const empty =
		tab === 'social'
			? 'Nothing yet. Replies, likes and follows on your posts show up here.'
			: tab === 'offers'
			? 'Nothing yet. Offers on your items and offers you win show up here.'
			: 'Nothing yet. Offers, accepted offers, replies, likes and follows show up here.'

	const dialogs = pending ? (
		pending.type === 'review' ? (
			<ReviewOfferModal
				key={`${pending.offer.id}-${pending.action}`}
				offer={pending.offer}
				itemId={pending.offer.inscription_id!}
				action={pending.action}
				surface="offer_inbox"
				onClose={() => setPending(null)}
				onChanged={() => {
					offers.markSeen()
					offers.refresh()
				}}
			/>
		) : pending.type === 'cancel' ? (
			<CancelOfferModal
				offer={pending.offer}
				onClose={() => setPending(null)}
				onChanged={() => offers.refresh()}
			/>
		) : (
			<MakeOfferModal
				open
				surface="offer_inbox"
				onClose={() => {
					setPending(null)
					setReplacing(null)
				}}
				inscriptionId={pending.offer.scope === 'item' ? pending.offer.inscription_id || undefined : undefined}
				collectionSlug={pending.offer.collection_slug || undefined}
				initialScope={pending.offer.scope}
				initialPriceSats={pending.offer.price_sats}
				initialTrait={
					pending.offer.scope === 'trait' && pending.offer.trait_type && pending.offer.trait_value
						? { type: pending.offer.trait_type, value: pending.offer.trait_value }
						: undefined
				}
				name={pending.offer.collection_slug || undefined}
				onPlaced={() => {
					const old = pending.offer
					setPending(null)
					offers.refresh()
					setReplacing(old)
				}}
			/>
		)
	) : replacing ? (
		<CancelOfferModal
			offer={replacing}
			onClose={() => setReplacing(null)}
			onChanged={() => offers.refresh()}
		/>
	) : null

	return (
		<>
		{dialogs}
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (next) {
					setNow(Date.now())
					offers.refresh()
				} else {
					// Seen once the panel closes, so the unread tint survives the read.
					social.markSeen()
					offers.markSeen()
				}
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					data-testid="notifications-bell"
					aria-label={unread > 0 ? `Notifications, ${unread} new` : 'Notifications'}
					className={cn(
						'ow-social relative grid size-10 shrink-0 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						className
					)}
				>
					<Bell className="size-[18px]" aria-hidden />
					{unread > 0 && (
						<span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none tabular-nums text-primary-foreground">
							{unread > 9 ? '9+' : unread}
						</span>
					)}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				sideOffset={8}
				data-testid="notifications-panel"
				className="ow-social w-[min(25rem,calc(100vw-1.5rem))] overflow-hidden p-0"
			>
				<div className="flex items-baseline justify-between border-b border-border px-4 py-3">
					<h2 className="text-sm font-semibold">Notifications</h2>
					{unread > 0 && <span className="text-xs text-muted-foreground">{unread} new</span>}
				</div>
				{tabs.length > 2 && (
					<div className="flex border-b border-border px-2" role="tablist" aria-label="Notification source">
						{tabs.map((t) => (
							<button
								key={t.id}
								type="button"
								role="tab"
								aria-selected={tab === t.id}
								data-testid={`notifications-tab-${t.id}`}
								onClick={() => setTab(t.id)}
								className={cn(
									'relative px-3 py-2 text-xs font-medium transition-colors',
									tab === t.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
								)}
							>
								{t.label}
								{t.count > 0 && (
									<span className="ml-1 rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold tabular-nums text-primary">
										{t.count > 9 ? '9+' : t.count}
									</span>
								)}
								{tab === t.id && (
									<span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-gradient-to-r from-purple-600 to-blue-500" />
								)}
							</button>
						))}
					</div>
				)}
				{entries.length === 0 ? (
					<p className="px-4 py-10 text-center text-sm text-muted-foreground">
						{loading ? 'Loading…' : empty}
					</p>
				) : (
					<ul className="max-h-[min(28rem,70dvh)] divide-y divide-border/60 overflow-y-auto overscroll-contain">
						{entries.map((e) => (
							<React.Fragment key={e.key}>{e.node}</React.Fragment>
						))}
					</ul>
				)}
				{offersOn && tab !== 'social' && <ManageOffersLink onDone={done} />}
			</PopoverContent>
		</Popover>
		</>
	)
}
